import { Button, Dialog, DialogContent, IconButton, Tooltip, Typography } from "@material-ui/core";
import SettingsIcon from '@material-ui/icons/Settings';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { useEffect, useState } from "react";
import { orderBy } from "lodash";
import Authorize from "components/Authorize";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import CustomInput from "components/CustomInput";
import LoadingSpinner from "components/LoadingSpinner";
import NotificationProvider from "components/NotificationProvider";
import { activityStatusById, activityStatusOrder } from "features/activity/activityConsts";
import { useGetTimeInStatusThresholdsQuery, useUpdateTimeInStatusThresholdsMutation } from "features/activity/activitySlice";
import { thresholdConfigConsts, thresholdLegendTiers } from "./thresholdConfigConsts";
import { thresholdConfigStyles } from "./ThresholdConfigStyles";

const ThresholdConfigDialog = () => {
    const classes = thresholdConfigStyles();

    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);

    const { data: thresholds, isFetching, isError } = useGetTimeInStatusThresholdsQuery(undefined, { skip: !open });
    const [updateThresholds, { isLoading: isSaving }] = useUpdateTimeInStatusThresholdsMutation();

    useEffect(() => {
        const orderedThresholds = orderBy((thresholds ?? []).map(threshold => ({ ...threshold, order: activityStatusOrder[threshold.woStatus] })), 'order');
        setRows(orderedThresholds.map(threshold => ({
            woStatus: threshold.woStatus,
            daysThreshold: threshold.daysThreshold ?? '',
        })));
    }, [thresholds]);

    const handleDaysChange = (woStatus) => (value) => {
        setRows(prev => prev.map(row => row.woStatus === woStatus
            ? { ...row, daysThreshold: value === '' ? '' : Number(value) }
            : row));
    };

    const handleSave = async () => {
        const payload = rows
            .filter(row => row.daysThreshold !== '' && row.daysThreshold != null)
            .map(row => ({ woStatus: row.woStatus, daysThreshold: Number(row.daysThreshold) }));
        const result = await updateThresholds(payload);
        if (result?.error) {
            NotificationProvider.error(thresholdConfigConsts.saveErrorLabel);
        } else {
            NotificationProvider.success(thresholdConfigConsts.saveSuccessLabel);
            setOpen(false);
        }
    };

    const legendIconClasses = {
        within: classes.legendIconWithin,
        approaching: classes.legendIconApproaching,
        breached: classes.legendIconBreached,
    };

    return (
        <>
            <Authorize profile={permissionProfiles.DASHBOARD.MECHANICAL_DASHBOARD_VIEW}>
                <Tooltip title={thresholdConfigConsts.title}>
                    <IconButton className={classes.triggerButton} onClick={() => setOpen(true)}><SettingsIcon /></IconButton>
                </Tooltip>
            </Authorize>
            <Dialog maxWidth='sm' fullWidth open={open} onClose={() => setOpen(false)}>
                <DialogContent>
                    <Typography className={classes.title}>{thresholdConfigConsts.title}</Typography>
                    {isFetching ? <LoadingSpinner loading={isFetching} /> :
                        isError ? <div className={classes.emptyState}>{thresholdConfigConsts.loadErrorLabel}</div> :
                        <>
                            {rows.length === 0 ?
                                <div className={classes.emptyState}>{thresholdConfigConsts.emptyStateLabel}</div> :
                                <div className={classes.table}>
                                    <div className={classes.headerRow}>
                                        <Typography className={classes.headerStatusCell}>{thresholdConfigConsts.statusColumnLabel}</Typography>
                                        <Typography className={classes.headerDaysCell}>{thresholdConfigConsts.daysColumnLabel}</Typography>
                                    </div>
                                    {rows.map(row => {
                                        const inputProps = {
                                            id: `threshold-${row.woStatus}`,
                                            elementType: "input",
                                            numberOnly: true,
                                            minValue: 0,
                                            value: row.daysThreshold,
                                        };
                                        return (
                                            <div key={row.woStatus} className={classes.tableRow}>
                                                <Typography className={classes.statusCell}>
                                                    {activityStatusById?.[row.woStatus]?.name ?? `Status ${row.woStatus}`}
                                                </Typography>
                                                <div className={classes.daysCell}>
                                                    <Authorize
                                                        profile={permissionProfiles.DASHBOARD.CONFIGURE_TIME_IN_STATUS_THRESHOLD}
                                                        readonlyComponent={
                                                            <Tooltip title={thresholdConfigConsts.readonlyTooltip}>
                                                                <div className={classes.readonlyInputWrapper}>
                                                                    <CustomInput {...inputProps} disabled />
                                                                </div>
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <CustomInput {...inputProps} onChange={handleDaysChange(row.woStatus)} />
                                                    </Authorize>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            }
                            <div className={classes.legend}>
                                <Typography className={classes.legendTitle}>{thresholdConfigConsts.legendTitle}</Typography>
                                {thresholdLegendTiers.map(tier => (
                                    <div key={tier.key} className={classes.legendRow}>
                                        <FiberManualRecordIcon className={`${classes.legendIcon} ${legendIconClasses[tier.key]}`} />
                                        <Typography><strong>{tier.label}:</strong> {tier.description}</Typography>
                                    </div>
                                ))}
                            </div>
                            <div className={classes.actions}>
                                <Authorize profile={permissionProfiles.DASHBOARD.CONFIGURE_TIME_IN_STATUS_THRESHOLD} readonlyComponent={<></>}>
                                    <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving}>
                                        {thresholdConfigConsts.saveLabel}
                                    </Button>
                                </Authorize>
                            </div>
                        </>
                    }
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ThresholdConfigDialog;
